import { forwardRef, type ReactNode, type ElementType } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/utils";

type Accent = "lime" | "mint" | "cyan" | "magenta" | "violet" | "sunburst" | "ribbon" | "none";

const accentBg: Record<Accent, string> = {
  lime: "before:bg-[radial-gradient(circle_at_top_right,hsl(var(--krew-lime)/0.18),transparent_55%)]",
  mint: "before:bg-[radial-gradient(circle_at_top_right,hsl(var(--krew-mint)/0.18),transparent_55%)]",
  cyan: "before:bg-[radial-gradient(circle_at_top_right,hsl(var(--krew-cyan)/0.22),transparent_55%)]",
  magenta: "before:bg-[radial-gradient(circle_at_top_right,hsl(var(--krew-magenta)/0.22),transparent_55%)]",
  violet: "before:bg-[radial-gradient(circle_at_top_right,hsl(var(--krew-violet)/0.22),transparent_55%)]",
  sunburst: "before:bg-[radial-gradient(circle_at_top_right,hsl(var(--krew-sunburst)/0.22),transparent_55%)]",
  ribbon: "before:bg-ribbon-soft",
  none: "",
};

interface BentoCardProps extends Omit<HTMLMotionProps<"div">, "ref"> {
  accent?: Accent;
  asChild?: boolean;
  interactive?: boolean;
  children: ReactNode;
  className?: string;
}

/** Modular bento tile — soft glass, subtle accent glow, optional hover lift. */
export const BentoCard = forwardRef<HTMLDivElement, BentoCardProps>(function BentoCard(
  { accent = "none", interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <motion.div
      ref={ref}
      whileHover={interactive ? { y: -2 } : undefined}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      className={cn(
        "group relative overflow-hidden rounded-3xl bg-card/70 backdrop-blur-xl shadow-soft",
        "border border-foreground/[0.06]",
        "before:pointer-events-none before:absolute before:inset-0 before:opacity-100 before:transition-opacity",
        accentBg[accent],
        interactive && "cursor-pointer hover:border-foreground/[0.12] hover:shadow-ribbon",
        className,
      )}
      {...rest}
    >
      <div className="relative z-[1] h-full w-full">{children}</div>
    </motion.div>
  );
});

export default BentoCard;